import { push } from '@takeshape/redux-little-router';
import type { PatternMetadata } from '@takeshape/schema';
import { getRoutineSaga } from 'app/common/utils/new-sagas';
import { branding } from 'app/config';
import yaml from 'js-yaml';
import type { SagaIterator } from 'redux-saga';
import { all, call, fork, put, take, takeLatest } from 'redux-saga/effects';
import { getUser } from '../auth/actions.js';
import { createBranchPath } from '../branches/utils.js';
import { type handleSuccessPayload, reportImportStatus } from '../common/saga.js';
import { IMPORT_STATUS } from '../common/types.js';
import { showMessage } from '../messages/actions.js';
import {
  fetchPatternMetadata,
  importComplete,
  importStatusRoutine,
  projectImportRoutine,
  setError,
  setImportStatus
} from './actions';
import { type GetProjectImportParams, type ImportProjectMutationParams, importProject } from './api.js';

const MANIFEST_FILE_NAME_YAML = 'pattern.yaml';
const MANIFEST_FILE_NAME_YML = 'pattern.yml';
const MANIFEST_FILE_NAME_JSON = 'pattern.json';

export function getRepoMetadataPath(repo: string, fileName: string): string {
  let path = repo;

  // Check if supported repo type
  if (!/^https?:\/\/github.com/.test(repo)) {
    throw new Error('Only GitHub repositories are supported at this time.');
  }

  // Strip trailing slash
  path = path.replace(/\/$/, '');

  // If URL doesn't specify location, assume they want root directory on master
  // This assumption may be wrong! If so, they need to specify a branch
  if (path.includes('/tree/')) {
    path = path.replace(/\/tree\//, '/');
  } else {
    path += '/master';
  }

  // Use path to raw file
  path = path.replace('github.com', 'raw.githubusercontent.com');

  return `${path}/${fileName}`;
}

// Get information about the pattern to display the initial informational page.
export function* fetchPatternMetadataSaga(payload: string): SagaIterator {
  const repo = payload;
  const manifestUrls = [];
  try {
    manifestUrls.push(getRepoMetadataPath(repo, MANIFEST_FILE_NAME_YAML));
    manifestUrls.push(getRepoMetadataPath(repo, MANIFEST_FILE_NAME_YML));
    manifestUrls.push(getRepoMetadataPath(repo, MANIFEST_FILE_NAME_JSON));
  } catch (error: any) {
    yield put(setError(error.message));
    return;
  }

  const failedToFetch = setError(
    `Could not find pattern metadata file for ${repo}. Make sure the repository URL is correct, that the repository is not private, and that it has a ${MANIFEST_FILE_NAME_YAML}, ${MANIFEST_FILE_NAME_YML} or ${MANIFEST_FILE_NAME_JSON}.`
  );

  let response;
  for (const url of manifestUrls) {
    try {
      response = yield call(fetch, url);
      if (response.status === 200) {
        break;
      }
    } catch {}
  }

  if (response === undefined || response.status !== 200) {
    yield put(failedToFetch);
    return;
  }

  const text = yield call([response, response.text]);
  try {
    const patternMetadata = yaml.safeLoad(text) as PatternMetadata | undefined;
    if (patternMetadata === undefined) {
      yield put(setError(`Empty ${MANIFEST_FILE_NAME_YAML} in ${repo}.`));
    } else {
      yield put(fetchPatternMetadata.fulfilled({ patternMetadata }));
    }
  } catch (yamlError) {
    console.error('Could not parse project info YAML', yamlError);
    yield put(setError(`Could not parse ${MANIFEST_FILE_NAME_YAML} in ${repo}.`));
  }
}

// Import the pattern to a new project.
export function* triggerImport(payload: { params: ImportProjectMutationParams }): SagaIterator {
  const { id } = yield call(importProject, payload.params);
  yield put(
    importStatusRoutine.requested({
      params: { id, projectId: payload.params.projectId }
    })
  );
}

export function* importPending(): SagaIterator {
  yield put(setImportStatus(IMPORT_STATUS.PENDING));
}

export function* importRejected({ payload }: { payload: string }): SagaIterator {
  const preamble = `Error adding to ${branding.productName}`;
  yield put(setImportStatus(IMPORT_STATUS.NOT_STARTED));
  yield put(showMessage('error', `${preamble}\r\n${payload}`));
}

// Poll for import status to display progress and success/failure.
export function* requestStatus(payload: { params: GetProjectImportParams }): SagaIterator {
  yield call(
    reportImportStatus,
    payload.params.id,
    setImportStatus,
    importComplete,
    payload.params.projectId === undefined
  );
}

export function* handleImportComplete({
  payload: { projectId, newProject }
}: {
  payload: handleSuccessPayload;
}): SagaIterator {
  // WatchImportSaga will handle new projects since we would also need to wait for getUser.FULFILLED
  if (!newProject) {
    yield put(push(`${createBranchPath(projectId)}/schema`));
  }
}

export function* watchImportSaga(): SagaIterator {
  while (true) {
    yield take(projectImportRoutine.requested);
    const [{ payload }]: [{ payload: handleSuccessPayload }] = yield all([
      take(importComplete),
      take(getUser.FULFILLED)
    ]);
    yield put(push(createBranchPath(payload.projectId)));
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(fetchPatternMetadata.requested, getRoutineSaga(fetchPatternMetadata, fetchPatternMetadataSaga)),
    takeLatest(projectImportRoutine.requested, getRoutineSaga(projectImportRoutine, triggerImport)),
    takeLatest(projectImportRoutine.pending, importPending),
    takeLatest(projectImportRoutine.rejected, importRejected),
    takeLatest(importStatusRoutine.requested, getRoutineSaga(importStatusRoutine, requestStatus)),
    takeLatest(importComplete, handleImportComplete),
    takeLatest(importStatusRoutine.rejected, importRejected),
    fork(watchImportSaga)
  ]);
}
